<template>
  <div class="subject-editor-container">
    <div v-if="label" class="subject-editor-header">
      <span class="subject-editor-label">{{ label }}</span>
    </div>
    <editor-content
      v-if="editor"
      class="subject-editor-content"
      :class="{ 'subject-editor-content--with-header': !!label }"
      :editor="editor"
    />
  </div>
</template>

<script>
import { Component, Vue, Watch, Prop } from 'vue-property-decorator'
import { getVariableColorScheme } from '../utils/variableColorScheme.js'
import '../utils/variableColorScheme.css'
import { Editor, EditorContent, VueRenderer } from '@tiptap/vue-2'
import { Extension } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import MentionList from './MentionList.vue'

@Component({
  name: 'SubjectEditor',
  components: {
    EditorContent
  }
})
class SubjectEditor extends Vue {
  @Prop({ type: String, default: '' })
  content

  @Prop({ type: String, default: '' })
  label

  @Prop({ type: Array, default: () => [] })
  mentionItems

  @Prop({ type: Array, default: () => [] })
  messageVariables

  editor = null

  mounted () {
    this.createEditor()
  }

  beforeDestroy () {
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
  }

  escapeHtml (value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  getVariableByTechnicalKey (technicalKey) {
    if (!technicalKey || !this.messageVariables) {
      return null
    }
    const keyLower = String(technicalKey).toLowerCase()
    for (const category of this.messageVariables) {
      if (!Array.isArray(category.variables)) {
        continue
      }
      const variable = category.variables.find(v => String(v.technicalKey).toLowerCase() === keyLower)
      if (variable) {
        return variable
      }
    }
    return null
  }

  getVariableLabelById (technicalKey) {
    const variable = this.getVariableByTechnicalKey(technicalKey)
    return variable?.displayLabel || variable?.displayTag || technicalKey
  }

  buildVariableChipHtml (technicalKey) {
    const variable = this.getVariableByTechnicalKey(technicalKey)
    const variableType = variable?.variableType || null
    const colorClass = getVariableColorScheme(variableType).colorClass
    const label = variable?.displayLabel || variable?.displayTag || technicalKey
    const safeKey = this.escapeHtml(technicalKey)
    const safeLabel = this.escapeHtml(label)
    const safeType = variableType ? this.escapeHtml(variableType) : null

    return `<span class="editor-variable-chip ${colorClass}" data-chip="${safeKey}"${safeType ? ` data-variable-type="${safeType}"` : ''} contenteditable="false">${safeLabel}</span>`
  }

  convertTemplatePlaceholdersToEditorHtml (html) {
    const sourceHtml = String(html || '')
    const parser = new window.DOMParser()
    const doc = parser.parseFromString(`<div>${sourceHtml}</div>`, 'text/html')
    const root = doc.body.firstElementChild || doc.body

    const walker = doc.createTreeWalker(root, window.NodeFilter.SHOW_TEXT)
    const textNodes = []
    let node = walker.nextNode()
    while (node) {
      if (node.nodeValue && node.nodeValue.includes('{{$')) {
        textNodes.push(node)
      }
      node = walker.nextNode()
    }

    const placeholderRegex = /\{\{\$([A-Za-z0-9_]+)\}\}/g
    textNodes.forEach(textNode => {
      const rawText = textNode.nodeValue || ''
      let hasMatch = false
      let lastIndex = 0
      const fragment = doc.createDocumentFragment()
      let match = placeholderRegex.exec(rawText)
      while (match) {
        hasMatch = true
        const fullMatch = match[0]
        const technicalKey = match[1]
        if (match.index > lastIndex) {
          fragment.appendChild(doc.createTextNode(rawText.slice(lastIndex, match.index)))
        }
        const tempWrapper = doc.createElement('div')
        tempWrapper.innerHTML = this.buildVariableChipHtml(technicalKey)
        if (tempWrapper.firstChild) {
          fragment.appendChild(tempWrapper.firstChild)
        }
        lastIndex = match.index + fullMatch.length
        match = placeholderRegex.exec(rawText)
      }
      if (!hasMatch) {
        return
      }
      if (lastIndex < rawText.length) {
        fragment.appendChild(doc.createTextNode(rawText.slice(lastIndex)))
      }
      textNode.parentNode.replaceChild(fragment, textNode)
      placeholderRegex.lastIndex = 0
    })
    return root.innerHTML
  }

  convertEditorHtmlToTemplatePlaceholders (html) {
    const sourceHtml = String(html || '')
    const parser = new window.DOMParser()
    const doc = parser.parseFromString(`<div>${sourceHtml}</div>`, 'text/html')
    const root = doc.body.firstElementChild || doc.body
    const chips = root.querySelectorAll('span[data-chip]')

    chips.forEach(chip => {
      const technicalKey = chip.getAttribute('data-chip')
      if (!technicalKey) {
        return
      }
      chip.replaceWith(doc.createTextNode(`{{$${technicalKey}}}`))
    })

    // Subject must be stored as plain text (with {{$KEY}} placeholders), never HTML.
    return String(root.textContent || '')
      .replace(/\u00A0/g, ' ')
      .replace(/[\r\n\t]+/g, ' ')
  }

  getSerializedContent () {
    if (!this.editor) {
      return this.convertEditorHtmlToTemplatePlaceholders(this.content || '')
    }
    return this.convertEditorHtmlToTemplatePlaceholders(this.editor.getHTML())
  }

  createEditor (initialContent = null) {
    const getVariableLabelById = this.getVariableLabelById.bind(this)

    const mentionExtension = Mention.extend({
      addAttributes () {
        return {
          id: {
            default: null,
            parseHTML: element => element.getAttribute('data-chip')
          },
          label: {
            default: null,
            parseHTML: element => {
              const textLabel = (element.textContent || '').trim()
              return textLabel || null
            }
          },
          variableType: {
            default: null,
            parseHTML: element => element.getAttribute('data-variable-type')
          },
          colorClass: {
            default: null,
            parseHTML: element => {
              const classAttr = element.getAttribute('class') || ''
              const classMatch = classAttr.match(/\bchip-[\w-]+\b/)
              return classMatch ? classMatch[0] : null
            }
          }
        }
      },
      parseHTML () {
        return [{ tag: 'span[data-chip]' }]
      },
      renderHTML ({ node }) {
        const variableInfo = this.options.editorInstance.getVariableByTechnicalKey(node.attrs.id)
        const variableType = node.attrs.variableType || variableInfo?.variableType || null
        const currentLabel = getVariableLabelById(node.attrs.id) || node.attrs.label || node.attrs.id || ''
        const typeColorClass = getVariableColorScheme(variableType).colorClass
        const colorClass = typeColorClass || node.attrs.colorClass || ''
        return [
          'span',
          {
            class: `editor-variable-chip ${colorClass}`.trim(),
            'data-chip': node.attrs.id,
            'data-variable-type': variableType || undefined,
            contenteditable: 'false'
          },
          currentLabel
        ]
      },
      renderText ({ node }) {
        return getVariableLabelById(node.attrs.id) || node.attrs.label || node.attrs.id || ''
      }
    }).configure({
      editorInstance: this,
      suggestion: this.getMentionSuggestion()
    })

    const extensions = [
      StarterKit.configure({
        bold: false,
        italic: false,
        strike: false,
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        horizontalRule: false
      }),
      Placeholder.configure({ placeholder: 'Digite o assunto...' }),
      mentionExtension
    ]

    this.editor = new Editor({
      content: this.convertTemplatePlaceholdersToEditorHtml(initialContent !== null ? initialContent : (this.content || '')),
      editable: true,
      extensions,
      onUpdate: ({ editor }) => {
        this.$emit('update:content', this.convertEditorHtmlToTemplatePlaceholders(editor.getHTML()))
      }
    })
  }

  getMentionSuggestion () {
    const getMentionAttrs = this.getMentionAttrs.bind(this)

    return {
      char: '@',
      allowSpaces: false,

      items: ({ query }) => {
        const normalized = (query || '').toLowerCase()
        const items = this.mentionItems.map(item => ({
          ...item,
          colorClass: getVariableColorScheme(item).colorClass
        }))

        if (!normalized) {
          return items.slice(0, 50)
        }

        return items.filter(item => {
          const label = (item.label || '').toLowerCase()
          const key = (item.id || '').toLowerCase()
          return label.includes(normalized) || key.includes(normalized)
        }).slice(0, 50)
      },

      command: ({ editor, range, props }) => {
        const attrs = this.getMentionAttrs(props)
        editor.chain().focus().insertContentAt(range, [
          { type: 'mention', attrs }
        ]).run()
      },

      render: () => {
        let component

        return {
          onStart: props => {
            component = new VueRenderer(MentionList, {
              parent: this,
              propsData: props
            })

            if (!props.clientRect) {
              return
            }

            component.element.style.position = 'absolute'
            component.element.style.zIndex = '9999'
            document.body.appendChild(component.element)

            const rect = props.clientRect()
            if (rect) {
              component.element.style.top = `${rect.bottom + window.scrollY}px`
              component.element.style.left = `${rect.left + window.scrollX}px`
            }
          },

          onUpdate: props => {
            component.updateProps(props)

            if (!props.clientRect) {
              return
            }

            const rect = props.clientRect()
            if (rect) {
              component.element.style.top = `${rect.bottom + window.scrollY}px`
              component.element.style.left = `${rect.left + window.scrollX}px`
            }
          },

          onKeyDown: props => {
            if (props.event.key === 'Escape') {
              if (component && component.element) {
                component.element.remove()
                component.destroy()
              }
              return true
            }

            return component.ref?.onKeyDown(props)
          },

          onExit: () => {
            if (component && component.element) {
              component.element.remove()
              component.destroy()
            }
          }
        }
      }
    }
  }

  getMentionAttrs (item) {
    return {
      id: item.id,
      label: item.label,
      variableType: item.variableType,
      colorClass: item.colorClass
    }
  }

  insertVariablePlaceholder (variable) {
    if (!this.editor || !variable) {
      return
    }

    const technicalKey = variable?.technicalKey || variable?.id
    const placeholder = `{{$${technicalKey}}}`
    const attrs = this.getMentionAttrs({
      id: technicalKey,
      label: variable.displayLabel || variable.displayTag || technicalKey,
      variableType: variable.variableType,
      colorClass: getVariableColorScheme(variable).colorClass
    })

    this.editor.chain()
      .focus()
      .insertContent([
        { type: 'mention', attrs }
      ])
      .run()
  }

  getEditorHtml () {
    return this.editor ? this.editor.getHTML() : ''
  }

  getEditorPlainText () {
    return this.editor ? this.editor.getText() : ''
  }

  hasContent () {
    return this.editor ? this.editor.getText().trim().length > 0 : false
  }
}

export default SubjectEditor
</script>

<style scoped lang="stylus">
.subject-editor-container
  border: 1px solid rgba(0, 0, 0, 0.23)
  border-radius: 4px
  background: white
  transition: border-color 0.2s

  &:focus-within
    border-color: #1976d2
    box-shadow: 0 0 0 1px rgba(25, 118, 210, 0.1)

.subject-editor-header
  padding: 4px 12px 2px

.subject-editor-label
  font-size: 12px
  color: rgba(0, 0, 0, 0.6)
  line-height: 1

.subject-editor-content
  padding: 6px 12px
  min-height: 28px
  font-size: 14px
  line-height: 1.3

.subject-editor-content--with-header
  padding-top: 4px

  ::v-deep p
    margin: 0

  ::v-deep .editor-variable-chip
    display: inline-flex
    align-items: center
    padding: 2px 6px
    border-radius: 16px
    background-color: #bbdefb
    color: #01579b
    font-weight: 400
    font-size: 12px
    cursor: default
    vertical-align: middle
    white-space: nowrap
    line-height: 1.2

  ::v-deep .editor-variable-chip.chip-yellow
    background-color: #fff9c4
    color: #f57f17

  ::v-deep .editor-variable-chip.chip-green
    background-color: #c8e6c9
    color: #2e7d32

  ::v-deep .editor-variable-chip.chip-purple
    background-color: #e1bee7
    color: #6a1b9a

  ::v-deep .editor-variable-chip.chip-blue
    background-color: #bbdefb
    color: #01579b

  ::v-deep .editor-variable-chip.chip-orange
    background-color: #ffe0b2
    color: #e65100

  ::v-deep .ProseMirror
    outline: none
    min-height: 20px
    white-space: nowrap
    overflow-x: auto
    overflow-y: hidden

    &:focus
      outline: none
</style>
